import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Avatar,
  Chip,
  Divider,
  Autocomplete,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { getInitials } from '../../utils/avatar';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import {
  classService,
  studentService,
  feeInvoiceService,
  feePaymentService,
} from '../../services/apiService';
import { showToast } from '../../utils/toast';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Cheque', 'UPI', 'Other'];

const FeeTransactionsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = 280;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const [classes, setClasses] = useState([]);
  const [studentsByClass, setStudentsByClass] = useState({});
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');

  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState({ invoiced: 0, paid: 0, outstanding: 0 });

  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState('');

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentCategory, setPaymentCategory] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount_paid: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'Cash',
    reference_number: '',
    remarks: '',
    billing_month: '',
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [billGenerating, setBillGenerating] = useState(false);


  useEffect(() => {
    const loadClasses = async () => {
      try {
        setLoading(true);
        const response = await classService.getAll();
        if (!response?.success) {
          throw new Error(response?.error || 'Failed to load classes');
        }
        setClasses(response.data || []);
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || 'Unable to load classes';
        setError(message);
        showToast.error(message);
      } finally {
        setLoading(false);
      }
    };
    loadClasses();
  }, []);

  const ensureStudentsLoaded = async (classId) => {
    if (!classId || studentsByClass[classId]) return;
    try {
      const response = await studentService.getAll({ class_id: classId });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to load students');
      }
      setStudentsByClass((prev) => ({
        ...prev,
        [classId]: response.data || [],
      }));
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load students';
      showToast.error(message);
    }
  };

  const loadStudentData = async (classId, studentId) => {
    if (!classId || !studentId) {
      setSummary(null);
      setPayments([]);
      setTotals({ invoiced: 0, paid: 0, outstanding: 0 });
      return;
    }
    try {
      setSummaryLoading(true);
      const [summaryRes, paymentsRes] = await Promise.all([
        feeInvoiceService.getStudentSummary({ class_id: classId, student_id: studentId }),
        feePaymentService.getAll({ class_id: classId, student_id: studentId }),
      ]);
      if (!summaryRes?.success) {
        throw new Error(summaryRes?.error || 'Failed to load summary');
      }
      if (!paymentsRes?.success) {
        throw new Error(paymentsRes?.error || 'Failed to load payments');
      }
      setSummary(summaryRes.data || null);
      const paymentList = paymentsRes.data || [];
      setPayments(paymentList);
      setSelectedPaymentIds([]);

      // Calculate Expected Amount: 12 × monthly frequency amounts + 1 × annual frequency amounts
      const categories = summaryRes.data?.categories || [];
      const expectedAmount = categories.reduce((sum, item) => {
        const amount = Number(item.billing_amount || 0);
        if (item.frequency === 'monthly') {
          return sum + amount * 12;
        } else if (item.frequency === 'annual') {
          return sum + amount * 1;
        }
        return sum;
      }, 0);

      const totals = categories.reduce(
        (acc, item) => {
          acc.paid += Number(item.total_paid || 0);
          acc.outstanding += Number(item.balance_due || 0);
          return acc;
        },
        { invoiced: expectedAmount, paid: 0, outstanding: 0 },
      );
      setTotals(totals);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load fee data';
      setError(message);
      showToast.error(message);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClass && selectedStudent) {
      loadStudentData(selectedClass, selectedStudent);
    } else {
      setSummary(null);
      setPayments([]);
      setTotals({ invoiced: 0, paid: 0, outstanding: 0 });
    }
  }, [selectedClass, selectedStudent]);

  const studentOptions = useMemo(
    () => studentsByClass[selectedClass] || [],
    [studentsByClass, selectedClass],
  );


  const handleClassChange = async (value) => {
    setSelectedClass(value);
    setSelectedStudent('');
    setSummary(null);
    setPayments([]);
    setTotals({ invoiced: 0, paid: 0, outstanding: 0 });
    setSelectedPaymentIds([]);
    if (value) {
      await ensureStudentsLoaded(value);
    }
  };

  const handleOpenPaymentDialog = (category) => {
    if (!selectedClass || !selectedStudent) {
      showToast.error('Select class and student first');
      return;
    }
    const now = new Date();
    setPaymentCategory(category);
    setPaymentForm({
      amount_paid: category.billing_amount || category.default_amount || 0,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: 'Cash',
      reference_number: '',
      remarks: '',
      billing_month: category.frequency === 'monthly' ? now.getMonth() + 1 : '',
    });
    setPaymentDialogOpen(true);
  };

  const handlePaymentFormChange = (field) => (event) => {
    setPaymentForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleRecordPayment = async () => {
    if (!paymentCategory) return;
    const amount = Number(paymentForm.amount_paid || 0);
    if (!amount || amount <= 0) {
      showToast.error('Enter a valid payment amount');
      return;
    }

    const payload = {
      class_id: selectedClass,
      student_id: selectedStudent,
      fee_category_id: paymentCategory.fee_category_id,
      amount_paid: amount,
      payment_date: paymentForm.payment_date,
      payment_method: paymentForm.payment_method,
      reference_number: paymentForm.reference_number || null,
      remarks: paymentForm.remarks || null,
    };

    const existingPayments = payments || [];
    const sessionValue = summary?.session || '';

    if (paymentCategory.frequency === 'monthly') {
      const billingMonth = Number(paymentForm.billing_month);
      if (!billingMonth || billingMonth < 1 || billingMonth > 12) {
        showToast.error('Select a valid billing month');
        return;
      }
      const duplicatePayment = existingPayments.some((payment) => (
        payment.student_id === selectedStudent
        && payment.fee_category_id === paymentCategory.fee_category_id
        && Number(payment.billing_month) === billingMonth
        && (payment.fee_session || '') === sessionValue
      ));
      if (duplicatePayment) {
        showToast.error('A payment for this month has already been recorded.');
        return;
      }
      payload.billing_month = billingMonth;
    } else {
      const duplicatePayment = existingPayments.some((payment) => (
        payment.student_id === selectedStudent
        && payment.fee_category_id === paymentCategory.fee_category_id
        && (payment.fee_session || '') === sessionValue
        && (!payment.billing_month || Number(payment.billing_month) === 0)
      ));
      if (duplicatePayment) {
        showToast.error('A payment for this session has already been recorded.');
        return;
      }
    }

    try {
      setSavingPayment(true);
      await feePaymentService.create(payload);
      showToast.success('Payment recorded');
      setPaymentDialogOpen(false);
      setPaymentCategory(null);
      // Preserve selectedClass and selectedStudent when reloading data
      const currentClass = selectedClass;
      const currentStudent = selectedStudent;
      await loadStudentData(currentClass, currentStudent);
      // Ensure filters remain set after reload
      setSelectedClass(currentClass);
      setSelectedStudent(currentStudent);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to record payment';
      showToast.error(message);
    } finally {
      setSavingPayment(false);
    }
  };

  const handleTogglePayment = (paymentId) => {
    setSelectedPaymentIds((prev) =>
      prev.includes(paymentId) ? prev.filter((id) => id !== paymentId) : [...prev, paymentId]
    );
  };

  const handleToggleAllPayments = (checked) => {
    if (checked) {
      setSelectedPaymentIds(payments.map((payment) => payment.id));
    } else {
      setSelectedPaymentIds([]);
    }
  };

  const selectedStudentRecord = useMemo(
    () => studentOptions.find((student) => student.id === selectedStudent) || null,
    [studentOptions, selectedStudent]
  );

  const handleGenerateBill = async () => {
    if (!selectedPaymentIds.length) {
      showToast.error('Please select at least one transaction');
      return;
    }
    try {
      setBillGenerating(true);
      const response = await feePaymentService.generateBill({ payment_ids: selectedPaymentIds });
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const studentLabel =
        selectedStudentRecord?.full_name?.replace(/[^a-z0-9]+/gi, '_').toLowerCase() || 'student';
      link.href = downloadUrl;
      const filename = `fee-bill-${studentLabel}-${new Date().toISOString().slice(0, 10)}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      showToast.success('Bill generated successfully');
      setSelectedPaymentIds([]);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Unable to generate bill';
      showToast.error(message);
    } finally {
      setBillGenerating(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: 1, borderColor: 'divider', mb: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Fee Transactions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Record payments against fee categories for each student.
                </Typography>
              </Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                flexWrap="wrap"
              >
                <Autocomplete
                  options={classes}
                  getOptionLabel={(option) => option ? `Class ${option.name}${option.section ? ` - ${option.section}` : ''}` : ''}
                  value={classes.find((cls) => cls.id === selectedClass) || null}
                  onChange={(event, newValue) => {
                    handleClassChange(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Class"
                      placeholder="Search and select class..."
                      size="small"
                    />
                  )}
                  sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { xs: '1 1 auto', sm: '0 1 auto' } }}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  noOptionsText="No classes found"
                />
                <Autocomplete
                  options={studentOptions}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    const rollNo = option.roll_number ? `${option.roll_number} - ` : '';
                    return `${rollNo}${option.full_name}`;
                  }}
                  value={studentOptions.find((student) => student.id === selectedStudent) || null}
                  onChange={(event, newValue) => {
                    setSelectedStudent(newValue ? newValue.id : '');
                  }}
                  disabled={!selectedClass}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Student"
                      placeholder="Search and select student..."
                      size="small"
                    />
                  )}
                  sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { xs: '1 1 auto', sm: '0 1 auto' } }}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  noOptionsText="No students found"
                />
                <Button
                  variant="outlined"
                  onClick={() => loadStudentData(selectedClass, selectedStudent)}
                  disabled={!selectedClass || !selectedStudent || summaryLoading}
                  startIcon={summaryLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Refresh
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Expected Amount
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                ₹{Number(totals.invoiced || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Total Paid
              </Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                ₹{Number(totals.paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Outstanding
              </Typography>
              <Typography variant="h6" fontWeight={700} color="error.main">
                ₹{Number(totals.outstanding || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </Paper>
          </Stack>

          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: 1, borderColor: 'divider', mb: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Fee Categories
            </Typography>
            {summaryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !summary ? (
              <Typography variant="body2" color="text.secondary">
                Select a class and student to view fee categories.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: { xs: 1, sm: 1.5 },
                }}
              >
                    {(summary.categories || []).map((category) => {
                  const billingAmount = Number(category.billing_amount || 0);
                  const totalPaid = Number(category.total_paid || 0);
                  const frequency = category.frequency || 'monthly';
                  
                  // Calculate expected amount based on frequency
                  const expectedAmount = frequency === 'monthly' ? billingAmount * 12 : billingAmount;
                  
                  // Calculate balance: expected amount - total paid
                  const balance = expectedAmount - totalPaid;
                  
                  // Status based on balance: if balance is 0 or less, mark as paid, else pending
                  const isPaid = balance <= 0;
                  const statusLabel = isPaid ? 'Paid' : 'Pending';
                      return (
                    <Paper
                      key={category.fee_category_id}
                      variant="outlined"
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 1.5,
                        borderColor: 'divider',
                      }}
                    >
                      {/* Desktop: Single row layout */}
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ display: { xs: 'none', sm: 'flex' } }}
                      >
                        {/* Category Name */}
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            flex: '0 1 200px',
                            minWidth: '150px',
                            fontSize: '0.875rem',
                          }}
                        >
                          {category.category_name}
                        </Typography>

                        {/* Category Details */}
                        <Typography
                          variant="body2"
                          sx={{
                            flex: '0 1 180px',
                            minWidth: '150px',
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                          }}
                        >
                              {category.charge_scope === 'student' ? 'Student' : 'Class'} level
                          {category.is_optional ? ' · Optional' : ''} · {category.frequency?.replace('_', ' ') || '—'}
                            </Typography>

                        {/* Status Chip */}
                        <Chip
                          label={statusLabel}
                          color={isPaid ? 'success' : 'warning'}
                          size="small"
                          variant="filled"
                          sx={{ minWidth: '70px' }}
                        />

                        {/* Amount */}
                        <Box sx={{ flex: '0 1 120px', textAlign: 'right' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Amount
                          </Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.875rem' }}>
                            ₹{expectedAmount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </Typography>
                        </Box>

                        {/* Total Paid */}
                        <Box sx={{ flex: '0 1 120px', textAlign: 'right' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Paid
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.875rem' }}>
                            ₹{totalPaid.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </Typography>
                        </Box>

                        {/* Balance */}
                        <Box sx={{ flex: '0 1 120px', textAlign: 'right' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Balance
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="error.main" sx={{ fontSize: '0.875rem' }}>
                            ₹{balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>

                        {/* Action Button */}
                        <Box sx={{ flex: '0 0 auto', ml: 'auto' }}>
                            <Button
                              size="small"
                            variant="contained"
                            startIcon={<PaymentIcon />}
                              onClick={() => handleOpenPaymentDialog(category)}
                            >
                              Record Payment
                            </Button>
                        </Box>
                      </Stack>

                      {/* Mobile: Compact layout */}
                      <Stack
                        spacing={1}
                        sx={{ display: { xs: 'flex', sm: 'none' } }}
                      >
                        {/* First row: Category Name + Status + Amount */}
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                              {category.category_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.25 }}>
                              {category.charge_scope === 'student' ? 'Student' : 'Class'} level
                              {category.is_optional ? ' · Optional' : ''} · {category.frequency?.replace('_', ' ') || '—'}
                            </Typography>
                          </Box>
                          <Chip
                            label={statusLabel}
                            color={isPaid ? 'success' : 'warning'}
                            size="small"
                            variant="filled"
                          />
                          <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.875rem' }}>
                            ₹{expectedAmount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </Typography>
                        </Stack>

                        {/* Second row: Amount, Paid, Balance */}
                        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              Amount
                            </Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                              ₹{expectedAmount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              Paid
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="success.main" sx={{ fontSize: '0.8rem' }}>
                              ₹{totalPaid.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              Balance
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="error.main" sx={{ fontSize: '0.8rem' }}>
                              ₹{balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Third row: Action Button */}
                        <Box>
                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            startIcon={<PaymentIcon />}
                            onClick={() => handleOpenPaymentDialog(category)}
                          >
                            Record Payment
                          </Button>
                        </Box>
                      </Stack>
                    </Paper>
                      );
                    })}
              </Box>
            )}
          </Paper>

          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: 1, borderColor: 'divider' }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Typography variant="h6" fontWeight={700}>
              Payment History
            </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedPaymentIds([])}
                    disabled={!selectedPaymentIds.length || billGenerating}
                    fullWidth={isMobile}
                    size="small"
                  >
                    Clear Selection
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleGenerateBill}
                    disabled={!selectedPaymentIds.length || billGenerating}
                    startIcon={billGenerating ? <CircularProgress size={18} /> : <ReceiptIcon />}
                    fullWidth={isMobile}
                    size="small"
                  >
                    {billGenerating ? 'Generating...' : 'Generate Bill'}
                  </Button>
                </Stack>
              </Stack>

              {/* Select All Checkbox */}
              {payments.length > 0 && (
                <Box>
                  <Checkbox
                    indeterminate={
                      selectedPaymentIds.length > 0 && selectedPaymentIds.length < payments.length
                    }
                    checked={payments.length > 0 && selectedPaymentIds.length === payments.length}
                    onChange={(event) => handleToggleAllPayments(event.target.checked)}
                    inputProps={{ 'aria-label': 'select all payments' }}
                  />
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    Select All ({selectedPaymentIds.length} selected)
                  </Typography>
                </Box>
              )}

                  {payments.length ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: { xs: 1, sm: 1.5 },
                  }}
                >
                  {payments.map((payment) => {
                      let period = '—';
                      if (payment.billing_month) {
                        const month = MONTH_OPTIONS.find((m) => m.value === Number(payment.billing_month));
                        period = month
                          ? `${month.label} ${payment.fee_session || ''}`
                          : `${payment.billing_month}/${payment.fee_session || ''}`;
                      } else if (payment.fee_session) {
                        period = payment.fee_session;
                      }
                    const isSelected = selectedPaymentIds.includes(payment.id);
                      return (
                      <Paper
                        key={payment.id}
                        variant="outlined"
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 1.5,
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          borderWidth: isSelected ? 2 : 1,
                        }}
                      >
                        {/* Desktop: Single row layout */}
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          sx={{ display: { xs: 'none', sm: 'flex' } }}
                        >
                          {/* Checkbox */}
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleTogglePayment(payment.id)}
                            inputProps={{ 'aria-label': `select payment ${payment.id}` }}
                            sx={{ p: 0.5 }}
                          />

                          {/* Date */}
                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: '90px',
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                            }}
                          >
                            {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : '—'}
                          </Typography>

                          {/* Category */}
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              flex: '0 1 150px',
                              minWidth: '120px',
                              fontSize: '0.875rem',
                            }}
                          >
                            {payment.category_name || '—'}
                          </Typography>

                          {/* Period */}
                          <Typography
                            variant="body2"
                            sx={{
                              flex: '0 1 120px',
                              minWidth: '100px',
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                            }}
                          >
                            {period}
                          </Typography>

                          {/* Method */}
                          <Typography
                            variant="body2"
                            sx={{
                              flex: '0 1 100px',
                              minWidth: '80px',
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                            }}
                          >
                            {payment.payment_method || '—'}
                          </Typography>

                          {/* Reference */}
                          <Typography
                            variant="body2"
                            sx={{
                              flex: '0 1 120px',
                              minWidth: '100px',
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={payment.reference_number || ''}
                          >
                            {payment.reference_number || '—'}
                          </Typography>

                          {/* Amount - takes remaining space and aligns right */}
                          <Box sx={{ flex: '1 1 auto', textAlign: 'right' }}>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color="success.main"
                              sx={{
                                fontSize: '0.875rem',
                              }}
                            >
                            ₹{Number(payment.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Mobile: Compact layout with better space utilization */}
                        <Stack
                          spacing={1}
                          sx={{ display: { xs: 'flex', sm: 'none' } }}
                        >
                          {/* First row: Checkbox + Category + Amount */}
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleTogglePayment(payment.id)}
                              inputProps={{ 'aria-label': `select payment ${payment.id}` }}
                              size="small"
                              sx={{ p: 0.5 }}
                            />
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                flex: 1,
                                fontSize: '0.875rem',
                              }}
                            >
                              {payment.category_name || '—'}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color="success.main"
                              sx={{
                                fontSize: '0.875rem',
                              }}
                            >
                              ₹{Number(payment.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </Typography>
                          </Stack>

                          {/* Second row: Date, Period, Method */}
                          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                              }}
                            >
                              {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : '—'}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                              }}
                            >
                              {period}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                              }}
                            >
                              {payment.payment_method || '—'}
                            </Typography>
                            {payment.reference_number && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '150px',
                                }}
                                title={payment.reference_number}
                              >
                                Ref: {payment.reference_number}
                              </Typography>
                            )}
                          </Stack>

                          {/* Third row: Remarks (if exists) */}
                          {payment.remarks && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                fontStyle: 'italic',
                              }}
                            >
                              {payment.remarks}
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                          No payment history available for this student.
                        </Typography>
                  )}
            </Stack>
          </Paper>

          <Dialog
            open={paymentDialogOpen}
            onClose={() => !savingPayment && setPaymentDialogOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                width: '100%',
                m: { xs: 1, sm: 3 },
              },
            }}
          >
            <DialogTitle>Record Payment for {paymentCategory?.category_name || ''}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField
                  label="Amount Paid"
                  type="number"
                  fullWidth
                  value={paymentForm.amount_paid}
                  onChange={handlePaymentFormChange('amount_paid')}
                  InputProps={{
                    startAdornment: <Typography variant="body2">₹</Typography>,
                  }}
                />
                <TextField
                  label="Payment Date"
                  type="date"
                  fullWidth
                  disabled={true}
                  value={paymentForm.payment_date}
                  onChange={handlePaymentFormChange('payment_date')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentForm.payment_method}
                    onChange={handlePaymentFormChange('payment_method')}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Reference Number"
                  fullWidth
                  placeholder="Enter transaction reference number"
                  value={paymentForm.reference_number}
                  onChange={handlePaymentFormChange('reference_number')}
                />
                <TextField
                  label="Remarks"
                  fullWidth
                  multiline
                  rows={2}
                  value={paymentForm.remarks}
                  onChange={handlePaymentFormChange('remarks')}
                />
                {paymentCategory?.frequency === 'monthly' ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="billing-month-select-label">Billing Month</InputLabel>
                      <Select
                        labelId="billing-month-select-label"
                        label="Billing Month"
                        value={paymentForm.billing_month}
                        onChange={(event) =>
                          setPaymentForm((prev) => ({
                            ...prev,
                            billing_month: Number(event.target.value),
                          }))
                        }
                      >
                        {MONTH_OPTIONS.map((month) => (
                          <MenuItem key={month.value} value={month.value}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                      Session: {summary?.session || '—'}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Session: {summary?.session || '—'}
                  </Typography>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleRecordPayment} variant="contained" color="primary" disabled={savingPayment}>
                {savingPayment ? <CircularProgress size={20} /> : 'Record Payment'}
              </Button>
            </DialogActions>
          </Dialog>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default FeeTransactionsPage;

